import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ExternalLink, Check, ShieldAlert, Calendar, MousePointerClick, Sparkles } from 'lucide-react';
import { fetchTool, fetchRelated, API } from '../api/client';

const pricingLabel = {
  free: 'Free',
  freemium: 'Freemium',
  paid: 'Paid',
  'open-source': 'Open source',
  unknown: 'Unknown',
};
const typeMeaning = {
  T: 'Free / open source tool',
  R: 'Requires registration',
  M: 'Paid or freemium',
  D: 'Underground — use at your own risk',
};

function resourceUrl(r) {
  if (r.resource_type === 'creator') return `/creator/${r.slug}`;
  if (r.resource_type === 'company') return `/company/${r.slug}`;
  return `/tool/${r.slug}`;
}

const badgeLabel = {
  verified: 'Verified',
  editors_pick: "Editor's Pick",
  trending_tiktok: 'Trending TikTok',
  trending_youtube: 'Trending YouTube',
  hidden_gem: 'Hidden Gem',
  weird_ai: 'Weird AI',
  beginner_friendly: 'Beginner Friendly',
  popular: 'Popular',
  new: 'New',
  rising_creator: 'Rising Creator',
};

export default function ToolPage() {
  const { slug } = useParams();
  const [tool, setTool] = useState(null);
  const [related, setRelated] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setTool(null);
    setErr(null);
    fetchTool(slug)
      .then(setTool)
      .catch(() => setErr('Tool not found'));
    fetchRelated(slug, 8).then(setRelated).catch(() => setRelated([]));
  }, [slug]);

  if (err) {
    return (
      <div className="page-container">
        <div className="empty-state">{err}</div>
        <Link to="/" className="pill-btn">Back to home</Link>
      </div>
    );
  }
  if (!tool) {
    return <div className="page-container"><div className="empty-state">Loading…</div></div>;
  }

  const isUnderground = tool.type_code === 'D';
  const initial = tool.name.trim().charAt(0).toUpperCase();

  return (
    <div className="page-container">
      <div className="crumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <Link to={`/category/${slugify(tool.primary_category)}`}>{tool.primary_category}</Link>
        {tool.subcategory && (
          <>
            <span>/</span>
            <span>{tool.subcategory}</span>
          </>
        )}
      </div>

      <div className="tool-page-head">
        <div className="tool-page-logo" aria-hidden>
          {tool.logo ? <img src={tool.logo} alt="" /> : <span>{initial}</span>}
        </div>
        <div className="tool-page-title">
          <h1>{tool.name}</h1>
          <p className="tool-page-desc">{tool.short_description || tool.primary_category}</p>
          <div className="tool-badges">
            <span className={`badge ${{ free: 'chip-t', freemium: 'chip-r', paid: 'chip-m', 'open-source': 'chip-t', unknown: '' }[tool.pricing_type]}`}>
              {pricingLabel[tool.pricing_type]}
            </span>
            {tool.free_trial && <span className="badge">Free trial</span>}
            {tool.featured && <span className="badge featured">Featured</span>}
            {tool.sponsored && <span className="badge sponsored">Sponsored</span>}
            {tool.verified && <span className="badge chip-t"><Check size={10} /> Verified</span>}
            {isUnderground && <span className="badge chip-d"><ShieldAlert size={10} /> Use with caution</span>}
            {tool.badges?.filter((b) => b !== 'verified').map((b) => (
              <span key={b} className="badge featured" data-testid={`tool-badge-${b}`}>
                <Sparkles size={9} /> {badgeLabel[b] || b.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </div>
        <a
          className="pill-btn primary lg"
          href={`${API}/go/${tool.slug}?ref=/tool/${tool.slug}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Visit website <ExternalLink size={14} />
        </a>
        <Link
          to={`/alternatives/${tool.slug}`}
          className="pill-btn lg"
          data-testid="tool-see-alternatives"
          style={{ marginLeft: 8 }}
        >
          See alternatives
        </Link>
      </div>

      <div className="tool-page-grid">
        <div className="tool-page-main">
          <h2>About</h2>
          <p>
            {tool.full_description ||
              tool.short_description ||
              `${tool.name} is an AI tool listed under ${tool.primary_category}${tool.subcategory ? ' \u2192 ' + tool.subcategory : ''}. This description has not been written yet.`}
          </p>
          {tool.ai_summary && (
            <div className="ai-summary-block" data-testid="tool-ai-summary">
              <div className="ai-summary-label">
                <Sparkles size={11} /> AI overview
                <span className="ai-summary-hint">Auto-generated. Verify important details on the tool&apos;s website.</span>
              </div>
              {tool.ai_summary.split('\n\n').map((para, i) => (
                <p key={`${tool.slug}-ai-p${i}`}>{para}</p>
              ))}
            </div>
          )}
          {tool.tags?.length > 0 && (
            <>
              <h3>Tags</h3>
              <div className="tag-list">
                {tool.tags.map((tag) => (
                  <span key={tag} className="chip">{tag}</span>
                ))}
              </div>
            </>
          )}
          {tool.type_code && (
            <>
              <h3>Access</h3>
              <p className="muted">{typeMeaning[tool.type_code]}</p>
            </>
          )}

          {related.length > 0 && (
            <>
              <h3 style={{ marginTop: 24 }}>People also viewed</h3>
              <ul className="supp-list" data-testid="related-list">
                {related.map((r) => {
                  const to = resourceUrl(r);
                  return (
                    <li key={r.slug}>
                      <Link to={to} className="supp-link">{r.name}</Link>
                      <span className="supp-cat">{r.primary_category}</span>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        <aside className="tool-page-side">
          <div className="side-block">
            <span className="muted">Rating</span>
            <div className="side-value">
              {tool.rating ? `${tool.rating.toFixed(1)} ★ (${tool.rating_count})` : 'Not yet rated'}
            </div>
          </div>
          <div className="side-block">
            <span className="muted">Category</span>
            <div className="side-value">
              <Link to={`/category/${slugify(tool.primary_category)}`}>{tool.primary_category}</Link>
              {tool.subcategory && <div className="sub-side">{tool.subcategory}</div>}
            </div>
          </div>
          <div className="side-block">
            <span className="muted"><MousePointerClick size={12} /> Total clicks</span>
            <div className="side-value">{tool.click_count.toLocaleString()}</div>
          </div>
          <div className="side-block">
            <span className="muted"><Calendar size={12} /> Added</span>
            <div className="side-value">{new Date(tool.date_added).toLocaleDateString()}</div>
          </div>
          <div className="side-block">
            <span className="muted">Website</span>
            <a
              className="side-link"
              href={`${API}/go/${tool.slug}?ref=/tool/${tool.slug}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {tool.website_url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
            </a>
          </div>
        </aside>
      </div>
    </div>
  );
}

function slugify(text) {
  return (text || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
}
